/**
 * LegalShell — the minimal Nav + Footer wrapper used by the compliance
 * pages (`/privacy`, `/terms`, `/contact`). Deliberately simpler than the
 * landing Nav: only a logo, a `Back to home` link, and the primary CTA.
 * The footer mirrors the landing footer meta so branding stays cohesive
 * across every public surface.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/brand/Logo";
import DemoNotice from "@/components/site/DemoNotice";

export function LegalNav() {
    return (
        <header
            className="sticky top-0 inset-x-0 z-40 bg-white/85 backdrop-blur-md border-b border-zinc-200"
            data-testid="legal-nav"
        >
            <div className="mx-auto max-w-4xl px-6 lg:px-8 flex items-center h-14">
                <Link to="/" className="flex items-center gap-2" data-testid="legal-logo">
                    <Logo size={26} />
                    <span className="font-display text-[15px] font-semibold tracking-tight">
                        YugOS
                    </span>
                </Link>
                <Link
                    to="/"
                    className="ml-6 hidden sm:inline-flex items-center gap-1 text-[13px] text-zinc-500 hover:text-zinc-950 transition-colors"
                    data-testid="legal-back-home"
                >
                    <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Back to home
                </Link>
                <div className="ml-auto flex items-center gap-2">
                    <Link to="/login">
                        <Button size="sm" className="h-8 text-[13px] group">
                            Launch YugOS
                            <ArrowUpRight
                                className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                strokeWidth={2}
                            />
                        </Button>
                    </Link>
                </div>
            </div>
        </header>
    );
}

export function LegalFooter() {
    return (
        <footer
            className="border-t border-zinc-200 bg-white"
            data-testid="legal-footer"
        >
            <div className="mx-auto max-w-4xl px-6 lg:px-8 py-10">
                <DemoNotice />
                <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2">
                            <Logo size={24} />
                            <span className="font-display text-[14px] font-semibold tracking-tight">
                                YugOS
                            </span>
                        </div>
                        <p className="mt-2 text-[12.5px] text-zinc-500 leading-relaxed max-w-md">
                            Built inside a real wholesale business.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-5 text-[12.5px] text-zinc-500">
                        <Link to="/privacy" className="hover:text-zinc-950 transition-colors">
                            Privacy Policy
                        </Link>
                        <Link to="/terms" className="hover:text-zinc-950 transition-colors">
                            Terms of Service
                        </Link>
                        <Link to="/contact" className="hover:text-zinc-950 transition-colors">
                            Contact
                        </Link>
                        <Link to="/login" className="hover:text-zinc-950 transition-colors">
                            Log in
                        </Link>
                    </div>
                </div>
                <div className="mt-6 pt-4 border-t border-zinc-100 text-[11.5px] text-zinc-400">
                    © {new Date().getFullYear()} YugOS
                </div>
            </div>
        </footer>
    );
}

/**
 * `LegalShell` — page wrapper. Renders nav + centred content column + footer.
 * Consumers just drop a `<LegalDocument>` (see below) inside.
 */
export default function LegalShell({ children }) {
    return (
        <div className="min-h-screen flex flex-col bg-white text-zinc-950" data-testid="legal-shell">
            <LegalNav />
            <main className="flex-1">{children}</main>
            <LegalFooter />
        </div>
    );
}

/**
 * `LegalDocument` — layout primitive that gives a compliance page its
 * eyebrow / title / effective-date header and a typographically clean
 * body column. Keeps every legal page visually identical.
 */
export function LegalDocument({ eyebrow, title, effective, children }) {
    return (
        <article className="mx-auto max-w-3xl px-6 lg:px-8 py-16 lg:py-24">
            <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-zinc-500">
                {eyebrow}
            </div>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-[44px] font-semibold tracking-[-0.02em] text-zinc-950 leading-[1.06]">
                {title}
            </h1>
            {effective && (
                <div className="mt-3 text-[12px] font-mono text-zinc-500">
                    Effective {effective}
                </div>
            )}
            <div className="mt-10 space-y-10 text-[15px] leading-[1.75] text-zinc-700">
                {children}
            </div>
        </article>
    );
}

/**
 * `LegalSection` — one titled section within a legal document. All body
 * text is rendered via this so we get uniform hierarchy across pages.
 */
export function LegalSection({ title, children }) {
    return (
        <section>
            <h2 className="font-display text-[19px] font-semibold text-zinc-950 tracking-tight">
                {title}
            </h2>
            <div className="mt-3 space-y-3">{children}</div>
        </section>
    );
}
